/**
 * Generation Mean Analysis results display.
 *
 * Renders the Cavalli (1952) 3-parameter and Hayman (1958) 6-parameter
 * model outputs plus the Mather & Jinks (1971) A/B/C/D scaling tests.
 * Reads the response shape produced by /api/analyze/generation-mean
 * (model=full).
 *
 * Niranjan, Feb 2026 — Phase B of biometrical-genetics scope.
 */
import React from 'react';
import { Sigma, FlaskConical, AlertTriangle, CheckCircle2 } from 'lucide-react';

const SIG_COLORS = {
  '***': '#0B5132',
  '**':  '#2D8B57',
  '*':   '#5B8E5C',
  '':    '#6B8F71',
};

const fmt = (v, d = 4) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return Number(v).toFixed(d);
};

function ParamRow({ p }) {
  const sigColor = SIG_COLORS[p.significance || ''];
  return (
    <tr className="hover:bg-[#F8FBF8] transition-colors">
      <td className="px-3 py-2 font-mono text-[13px] text-[#1F4E2E] font-semibold">{p.symbol}</td>
      <td className="px-3 py-2 text-[12px] text-[#6B8F71] italic">{p.name}</td>
      <td className="px-3 py-2 text-right font-mono text-[13px] text-[#1F4E2E]" style={{ color: sigColor, fontWeight: 600 }}>
        {fmt(p.estimate)}
      </td>
      <td className="px-3 py-2 text-right font-mono text-[12px] text-[#6B8F71]">± {fmt(p.se)}</td>
      <td className="px-3 py-2 text-right font-mono text-[12px]">{fmt(p.t, 3)}</td>
      <td className="px-3 py-2 text-right font-mono text-[12px]">{fmt(p.p_value)}</td>
      <td className="px-3 py-2 text-center font-mono text-[13px] font-bold" style={{ color: sigColor }}>
        {p.significance || 'ns'}
      </td>
    </tr>
  );
}

function ParamTable({ title, params, footerNote }) {
  if (!params || !params.length) return null;
  return (
    <div className="border border-[#E0EDE0] rounded-xl overflow-hidden bg-white">
      <div className="px-4 py-2.5 bg-[#F4FAF5] border-b border-[#E0EDE0]">
        <h5 className="text-[13px] font-bold text-[#1F4E2E]">{title}</h5>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-[#6B8F71] bg-white border-b border-[#E0EDE0]">
              <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider">Symbol</th>
              <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider">Effect</th>
              <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">Estimate</th>
              <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">SE</th>
              <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">t</th>
              <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">p</th>
              <th className="px-3 py-2 text-center text-[11px] font-bold uppercase tracking-wider">Sig</th>
            </tr>
          </thead>
          <tbody>{params.map((p) => <ParamRow key={p.symbol} p={p} />)}</tbody>
        </table>
      </div>
      {footerNote && (
        <div className="px-4 py-2 bg-[#FCFEFD] text-[11px] italic text-[#6B8F71] leading-relaxed">
          {footerNote}
        </div>
      )}
    </div>
  );
}

export default function GenerationMeanSection({ data }) {
  if (!data || !data.three_param_model) return null;

  const three = data.three_param_model;
  const six   = data.six_param_model || {};
  const sc    = data.scaling_tests || {};
  const ga    = data.gene_action || {};
  const chi   = three.chi_square || {};
  const adequate = chi.adequate === true;

  return (
    <div className="space-y-5" data-testid="genmean-section">
      <h4 className="text-base font-bold text-[#1F4E2E] border-b-2 border-[#2D8B57] pb-1.5 mb-3">
        Generation Mean Analysis &mdash; Cavalli (1952) &amp; Hayman (1958)
      </h4>

      {/* Generation means table */}
      {data.generation_means && data.generation_means.length > 0 && (
        <div className="border border-[#E0EDE0] rounded-xl overflow-hidden bg-white" data-testid="genmean-table">
          <div className="px-4 py-2.5 bg-[#F4FAF5] border-b border-[#E0EDE0]">
            <h5 className="text-[13px] font-bold text-[#1F4E2E]">Generation means</h5>
          </div>
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-[#6B8F71] bg-white border-b border-[#E0EDE0]">
                <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider">Generation</th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">Mean</th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">SD</th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">SE(mean)</th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">n</th>
              </tr>
            </thead>
            <tbody>
              {data.generation_means.filter((r) => r.mean !== null).map((r) => (
                <tr key={r.Generation} className="hover:bg-[#F8FBF8]">
                  <td className="px-3 py-2 font-mono text-[12px] text-[#1F4E2E] font-semibold">{r.Generation}</td>
                  <td className="px-3 py-2 text-right font-mono text-[12px]">{fmt(r.mean, 3)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[11px] text-[#6B8F71]">{fmt(r.std, 3)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[11px] text-[#6B8F71]">{fmt(r.se, 4)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[11px]">{r.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Chi-square verdict card */}
      <div
        className="rounded-xl p-4 flex items-start gap-3"
        style={{
          background: adequate ? '#EAF7EE' : '#FFF4E5',
          border: `1.5px solid ${adequate ? '#2D8B57' : '#D97706'}`,
        }}
        data-testid="genmean-verdict"
      >
        <div className="shrink-0 mt-0.5" style={{ color: adequate ? '#0B5132' : '#92400E' }}>
          {adequate ? <CheckCircle2 size={22} strokeWidth={2} /> : <AlertTriangle size={22} strokeWidth={2} />}
        </div>
        <div className="flex-1">
          <p className="text-[13px] font-bold mb-1" style={{ color: adequate ? '#0B5132' : '#92400E' }}>
            {adequate
              ? "Cavalli's joint scaling test: 3-parameter additive-dominance model is ADEQUATE"
              : "Cavalli's joint scaling test: 3-parameter model REJECTED — epistasis likely"}
          </p>
          <p className="text-[12px] text-[#3C4043] mb-1">
            Chi&sup2; = <span className="font-mono font-bold">{fmt(chi.value, 3)}</span>
            &nbsp;&middot;&nbsp; df = <span className="font-mono">{chi.df}</span>
            &nbsp;&middot;&nbsp; p = <span className="font-mono font-bold">{fmt(chi.p_value, 4)}</span>
          </p>
          <p className="text-[11px] italic text-[#6B8F71]">{three.note}</p>
        </div>
      </div>

      {/* Cavalli 3-param table */}
      <ParamTable
        title="Cavalli (1952) — 3-parameter additive-dominance model"
        params={three.parameters}
        footerNote={three.reference}
      />

      {/* 5-parameter Mather & Jinks model — drops [j] */}
      {Array.isArray(data.five_param_model?.parameters) && (
        <div className="space-y-2">
          <ParamTable
            title="Mather & Jinks (1971) — 5-parameter digenic model (without [j])"
            params={data.five_param_model.parameters}
            footerNote={data.five_param_model.reference}
          />
          {data.five_param_model.chi_square && (
            <div
              className="rounded-md text-[12px] px-3 py-2"
              style={{
                background: data.five_param_model.chi_square.adequate ? '#EAF7EE' : '#FFF4E5',
                border: `1px solid ${data.five_param_model.chi_square.adequate ? '#2D8B57' : '#D97706'}`,
                color: data.five_param_model.chi_square.adequate ? '#0B5132' : '#92400E',
              }}
              data-testid="genmean-5param-chi"
            >
              <strong>5-parameter chi&sup2; = {fmt(data.five_param_model.chi_square.value, 3)}</strong>
              {' '}(df = {data.five_param_model.chi_square.df}, p = {fmt(data.five_param_model.chi_square.p_value, 4)})
              {' — '}
              {data.five_param_model.chi_square.adequate
                ? 'fits adequately. The [j] term may be safely dropped.'
                : '5-param model REJECTED — [j] term needed (use 6-param Hayman).'}
            </div>
          )}
        </div>
      )}

      {/* Hayman 6-param table */}
      {Array.isArray(six.parameters) && six.parameters.length === 6 ? (
        <ParamTable
          title="Hayman (1958) — 6-parameter digenic model with epistasis"
          params={six.parameters}
          footerNote={six.reference + ' · ' + (six.chi_square?.note || '')}
        />
      ) : six.error ? (
        <div className="rounded-lg border border-[#E0E5DC] bg-[#FAFAF7] p-3 text-[12px] text-[#6B8F71] italic">
          <strong className="text-[#5C5A56] not-italic">Hayman 6-param:</strong> {six.error}
        </div>
      ) : null}

      {/* Model comparison panel */}
      {data.model_comparison?.rows?.length > 0 && (
        <div className="border-2 border-[#7C3AED] rounded-xl overflow-hidden bg-white" data-testid="genmean-comparison">
          <div className="px-4 py-2.5 bg-[#FAF8FF] border-b border-[#E2D6F5]">
            <h5 className="text-[13px] font-bold text-[#5B21B6] flex items-center gap-2">
              <Sigma size={14} /> Model comparison &mdash; best-fit recommendation
            </h5>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr className="text-[#6B8F71] border-b border-[#E0EDE0]">
                  <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider">Model</th>
                  <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider">Parameters</th>
                  <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">k</th>
                  <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">Chi²</th>
                  <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">df</th>
                  <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">p</th>
                  <th className="px-3 py-2 text-center text-[11px] font-bold uppercase tracking-wider">Adequate?</th>
                </tr>
              </thead>
              <tbody>
                {data.model_comparison.rows.map((r) => {
                  const isRec = r.model === data.model_comparison.recommended_model;
                  return (
                    <tr
                      key={r.model}
                      className={isRec ? 'bg-[#F0EAFA]' : 'hover:bg-[#FAF8FF]'}
                    >
                      <td className="px-3 py-2 text-[12px] font-bold text-[#1F4E2E]">
                        {isRec && <span className="text-[#7C3AED] mr-1">★</span>}
                        {r.model}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-[#6B8F71]">{r.parameters}</td>
                      <td className="px-3 py-2 text-right font-mono text-[11px]">{r.n_params}</td>
                      <td className="px-3 py-2 text-right font-mono text-[12px]">{fmt(r.chi_square, 3)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[11px]">{r.df ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-[12px]">{r.p_value === null || r.p_value === undefined ? '—' : fmt(r.p_value, 4)}</td>
                      <td className="px-3 py-2 text-center font-mono text-[12px] font-bold">
                        {r.adequate === true && <span className="text-[#0B5132]">✓</span>}
                        {r.adequate === false && <span className="text-[#9F1239]">✗</span>}
                        {r.adequate === null && <span className="text-[#6B8F71]">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-[#FAF8FF] border-t border-[#E2D6F5]">
            <div className="text-[12px] font-bold text-[#5B21B6] mb-1 flex items-center gap-1.5">
              <span>★</span> Recommended: {data.model_comparison.recommended_model}
            </div>
            <p className="text-[11px] text-[#3C4043] italic leading-relaxed">
              {data.model_comparison.recommendation_reason}
            </p>
          </div>
        </div>
      )}

      {/* Mather-Jinks scaling tests */}
      {Object.keys(sc).filter((k) => sc[k] && typeof sc[k] === 'object' && 'value' in sc[k]).length > 0 && (
        <div className="border border-[#E0EDE0] rounded-xl overflow-hidden bg-white" data-testid="genmean-scaling">
          <div className="px-4 py-2.5 bg-[#F4FAF5] border-b border-[#E0EDE0]">
            <h5 className="text-[13px] font-bold text-[#1F4E2E] flex items-center gap-2">
              <Sigma size={14} /> Mather &amp; Jinks (1971) scaling tests
            </h5>
          </div>
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-[#6B8F71] border-b border-[#E0EDE0]">
                <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider">Test</th>
                <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider">Formula</th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">Value</th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">SE</th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">t</th>
                <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider">p</th>
                <th className="px-3 py-2 text-center text-[11px] font-bold uppercase tracking-wider">Sig</th>
              </tr>
            </thead>
            <tbody>
              {['A', 'B', 'C', 'D'].filter((k) => sc[k]).map((k) => {
                const t = sc[k];
                return (
                  <tr key={k} className="hover:bg-[#F8FBF8]">
                    <td className="px-3 py-2 font-mono text-[13px] font-bold text-[#1F4E2E]">{k}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#6B8F71]">{t.formula}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px]">{fmt(t.value, 3)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] text-[#6B8F71]">± {fmt(t.se, 3)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px]">{fmt(t.t, 3)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px]">{fmt(t.p_value, 4)}</td>
                    <td className="px-3 py-2 text-center font-mono text-[12px] font-bold"
                        style={{ color: t.significant ? '#9F1239' : '#6B8F71' }}>
                      {t.significant ? '*' : 'ns'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Gene-action verdict */}
      {ga.verdict && (
        <div className="rounded-xl bg-gradient-to-br from-[#F4FAF5] to-[#EAF7EE] border border-[#2D8B57] p-4" data-testid="genmean-gene-action">
          <div className="flex items-center gap-2 mb-2">
            <FlaskConical size={16} className="text-[#0B5132]" />
            <h5 className="text-[14px] font-bold text-[#0B5132]">Gene action interpretation</h5>
          </div>
          <p className="text-[14px] text-[#1F4E2E] font-semibold mb-2">{ga.verdict}</p>
          {ga.degree_of_dominance_h_over_d !== null && (
            <p className="text-[12px] text-[#3C4043] mb-1">
              Degree of dominance: <span className="font-mono font-bold">|h/d| = {fmt(ga.degree_of_dominance_h_over_d, 2)}</span>
              {' '}
              <span className="text-[#6B8F71]">
                (&lt; 0.2 → additive · 0.2-1 → partial dominance · 1-2 → over-dominance · &gt; 2 → strong over-dominance)
              </span>
            </p>
          )}
          <p className="text-[12px] text-[#3C4043] italic">
            <strong className="not-italic">Breeding recommendation:</strong> {ga.breeding_implication}
          </p>
        </div>
      )}

      {/* References */}
      <div className="text-[11px] italic text-[#6B8F71] leading-relaxed pt-2 border-t border-[#F0F2F0]">
        {data.reference_notes}
      </div>
    </div>
  );
}
